import { apiDeleteRequest, apiGetRequest, apiPostRequest, apiPutRequest, logCourseAction } from "@/functions/requests";
import { useApp, useDynamoClient } from "@/hooks/contextHooks";
import { DynamoDBClient, QueryCommand } from "@aws-sdk/client-dynamodb";
import { 
	Button, 
	Card, 
	CardBody, 
	CardHeader, 
	Center, 
	Code, 
	Flex, 
	Heading, 
	Spinner, 
	Text, 
	Box, 
	Badge, 
	Divider, 
	useColorModeValue, 
	useToast,
	VStack,
	HStack,
	AlertDialog,
	AlertDialogBody,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogContent,
	AlertDialogOverlay
} from "@chakra-ui/react";
import { 
	faRocket, 
	faTrash, 
	faLock, 
	faGlobe, 
	faShieldHalved, 
	faExclamationTriangle,
	faEye
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useRef } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { AppMeta } from "@xnav/types/src/common";
import { fetchUserAttributes } from "@aws-amplify/auth";

enum deployStateEnum {
	undeployed = 0,
	deploying = 1,
	deployed = 2
}

export default function Deploy() {
	const app = useApp() as AppMeta
	const [editApp, setEditApp] = useState(app)
	const dynamo = useDynamoClient() as DynamoDBClient
	const [deployState, setDeployState] = useState(deployStateEnum.undeployed)
	const { user } = useAuthenticator()
	const [userRole, setUserRole] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
	const cancelRef = useRef<HTMLButtonElement>(null);
	const toast = useToast();

	// Sync editApp when app changes in context
	useEffect(() => {
		if (app) {
			setEditApp(app);
		}
	}, [app]);

	useEffect(() => {
		const fetchRole = async () => {
			if (user?.signInDetails?.loginId) {
				try {
					const data = await apiGetRequest<undefined, { role: string }>(`/users/${user.signInDetails.loginId}`);
					setUserRole(data?.role || 'Instructor');
				} catch (error) {
					console.error("Failed to fetch user role", error);
					setUserRole('Instructor');
				}
			}
		};
		fetchRole();
	}, [user]);

	async function handleDeploymentClick() {
		if (!app) return
		setDeployState(deployStateEnum.deploying)
		try {
			const { preferred_username, email } = await fetchUserAttributes()
			const timestamp = new Date().toString()
			await apiPutRequest<AppMeta, undefined>(`/apps/${editApp.id}`, {
				...editApp,
				creator: preferred_username ?? email!,
				edited: timestamp,
				deployed: timestamp
			})
			await apiPostRequest<any, undefined>(`/deploy`, {
				"id": {
					"S": editApp.id
				},
				"name": {
					"S": editApp.name
				},
				"creator": {
					"S": user.userId
				},
				"courseType":{
					"S": editApp.courseType
				},
				"visibility": {
					"S": editApp.visibility || (editApp.org || editApp.orgId || editApp.hiddenCode ? 'organization' : 'public')
				},
				...(editApp.orgId ? { "orgId": { "S": editApp.orgId } } : {}),
				...(editApp.org ? { "org": { "S": editApp.org } } : {}),
				...(editApp.hiddenCode ? { "accessCode": { "S": editApp.hiddenCode ?? '' } } : {}),
				...(editApp.category ? { "category": { "S": editApp.category } } : {}),
				...(editApp.tags && editApp.tags.length > 0 ? { "tags": { "SS": editApp.tags } } : {})
			})
			setDeployState(deployStateEnum.deployed)
			logCourseAction(editApp.id, "Course Deployed", `Successfully deployed course "${editApp.name}" to the mobile app.`);
			toast({
				title: "Deployment Successful",
				description: `"${editApp.name}" has been successfully deployed to the mobile app.`,
				status: "success",
				duration: 4000,
				isClosable: true,
				position: "top-right"
			});
		} catch (error) {
			console.error("Failed to deploy", error);
			setDeployState(deployStateEnum.undeployed);
			toast({
				title: "Deployment Failed",
				description: "An error occurred while deploying the course. Please try again.",
				status: "error",
				duration: 5000,
				isClosable: true,
				position: "top-right"
			});
		}
	}

	async function handleDeleteDeployment() {
		if (!app) return;
		setIsDeleting(true);
		setIsDeleteAlertOpen(false);
		try {
			await apiDeleteRequest<undefined>(`/deploy/${app.id}`);
			logCourseAction(app.id, "Deployment Deleted", `Removed course "${editApp.name}" deployment from the live mobile app.`);
			toast({
				title: "Deployment Deleted",
				description: `Deployment for "${editApp.name}" has been successfully removed from the live mobile app.`,
				status: "info",
				duration: 4000,
				isClosable: true,
				position: "top-right"
			});
		} catch (error) {
			console.error("Failed to delete deployment", error);
			toast({
				title: "Deletion Failed",
				description: "An error occurred while deleting the deployment. Please try again.",
				status: "error",
				duration: 5000,
				isClosable: true,
				position: "top-right"
			});
		} finally {
			setIsDeleting(false);
		}
	}

	useEffect(() => {
		if (!app) return;
		const command = new QueryCommand({
			TableName: 'xnav-4-deployed-apps',
			IndexName: 'CouseNameIndex',
			KeyConditionExpression: "#name = :nameVal",
			ExpressionAttributeNames: {
				'#name': 'name'
			},
			ExpressionAttributeValues: {
				':nameVal': {
					'S': app.name
				}
			}
		})
		dynamo.send(command).then((r) => console.log("Result", r)).catch(e => console.error("Error checking deployed app", e))
	}, [app, dynamo])

	// Theme color values
	const cardBg = useColorModeValue("white", "#101622");
	const cardBorder = useColorModeValue("gray.200", "rgba(255, 255, 255, 0.07)");
	const titleColor = useColorModeValue("gray.800", "white");
	const subtitleColor = useColorModeValue("gray.600", "gray.400");

	if (deployState === deployStateEnum.deploying) return (
		<Center w='100%' h='100%'>
			<Card 
				p={8} 
				borderRadius="2xl" 
				bg={cardBg} 
				border="1px solid" 
				borderColor={cardBorder} 
				boxShadow="2xl"
				maxW="500px"
				textAlign="center"
			>
				<CardHeader pb={2}>
					<Heading size="md" display="flex" alignItems="center" justifyContent="center" gap={3} color={titleColor}>
						<Spinner size="md" color="#0ea5e9" thickness="3px" />
						Deploying {app.name}
					</Heading>
				</CardHeader>
				<CardBody>
					<Text fontSize='md' color={subtitleColor}>
						Compiling resources and uploading to the global distribution network...
					</Text>
				</CardBody>
			</Card>
		</Center>
	)

	if (deployState === deployStateEnum.deployed) return (
		<Center w='100%' h='100%'>
			<Card 
				p={8} 
				borderRadius="2xl" 
				bg={cardBg} 
				border="1px solid" 
				borderColor={cardBorder} 
				boxShadow="2xl"
				maxW="500px"
				textAlign="center"
			>
				<CardHeader pb={2}>
					<Heading size="lg" bgGradient="linear(to-r, #38bdf8, #0ea5e9, #10b981)" bgClip="text" fontWeight="black" letterSpacing="tight">
						Success!
					</Heading>
				</CardHeader>
				<CardBody display='flex' flexDir='column' alignItems='center' gap={4}>
					<Box p={3} bg="rgba(16, 185, 129, 0.12)" color="#10b981" borderRadius="full" fontSize="2xl" w="60px" h="60px" display="flex" alignItems="center" justifyContent="center">
						<FontAwesomeIcon icon={faRocket} />
					</Box>
					<Text fontSize='md' fontWeight="semibold" color={titleColor}>
						Application deployed successfully!
					</Text>
					<Button 
						bgGradient="linear(to-r, #0284c7, #0ea5e9)" 
						_hover={{ bgGradient: "linear(to-r, #0369a1, #0284c7)", shadow: "0 4px 14px rgba(14, 165, 233, 0.35)" }}
						color="white"
						borderRadius="xl" 
						onClick={() => setDeployState(deployStateEnum.undeployed)}
					>
						Back to Dashboard
					</Button>
				</CardBody>
			</Card>
		</Center>
	)

	if (userRole === 'Instructor') {
		return (
			<Center w='100%' h='100%'>
				<Card 
					maxW="600px" 
					textAlign="center" 
					p={8} 
					borderRadius="2xl"
					bg={cardBg}
					border="1px solid"
					borderColor={cardBorder}
					boxShadow="2xl"
				>
					<Box p={4} bg="rgba(239, 68, 68, 0.1)" color="red.400" borderRadius="full" w="60px" h="60px" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={4}>
						<FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
					</Box>
					<Heading size="lg" color="red.400" mb={4}>Unauthorized Action</Heading>
					<Text fontSize="md" color={subtitleColor}>
						Instructors do not have permission to deploy or delete courses. 
						Please contact your Organization Admin or Super Admin to deploy this course to the live mobile app.
					</Text>
				</Card>
			</Center>
		)
	}

	const isPublic = editApp.visibility === 'public';
	const hasDomainRestr = !!editApp.orgId;
	const hasAccessCode = !!editApp.hiddenCode;

	return (
		<>
		<Center w='100%' h='100%' p={4}>
			<Card 
				w='100%' 
				maxW='850px' 
				p={8} 
				borderRadius="2xl" 
				bg={cardBg} 
				border="1px solid" 
				borderColor={cardBorder} 
				boxShadow="2xl"
			>
				<CardHeader display="flex" flexDir="column" alignItems="center" pb={6}>
					<Heading 
						bgGradient="linear(to-r, #38bdf8, #0ea5e9, #10b981)" 
						bgClip="text" 
						size="xl" 
						fontWeight="black"
						textAlign="center"
						letterSpacing="tight"
						mb={2}
					>
						Deploy {app.name}
					</Heading>
					<Text color={subtitleColor} fontSize="md" textAlign="center">
						Manage server synchronization and visibility settings for this course
					</Text>
				</CardHeader>

				<CardBody display='flex' flexDir='column' gap={6}>
					{/* Visibility Settings Banner */}
					{isPublic ? (
						<Box
							p={5}
							borderRadius="2xl"
							bg={useColorModeValue("rgba(16, 185, 129, 0.06)", "rgba(16, 185, 129, 0.08)")}
							border="1px solid"
							borderColor={useColorModeValue("green.200", "rgba(16, 185, 129, 0.25)")}
							w="100%"
						>
							<Flex align="center" gap={4}>
								<Flex
									p={3}
									bg={useColorModeValue("green.500", "rgba(16, 185, 129, 0.2)")}
									color={useColorModeValue("white", "#10b981")}
									borderRadius="xl"
									align="center"
									justify="center"
									w="46px"
									h="46px"
									flexShrink={0}
								>
									<FontAwesomeIcon icon={faGlobe} size="lg" />
								</Flex>
								<Box>
									<Heading size="xs" color={useColorModeValue("green.800", "green.300")} mb={1} fontWeight="bold">
										Visibility: Public Access
									</Heading>
									<Text fontSize="sm" color={useColorModeValue("green.700", "green.400")}>
										This course is open to anyone. Students can search and join this course on the mobile app without restrictions.
									</Text>
								</Box>
							</Flex>
						</Box>
					) : (
						<Box
							p={5}
							borderRadius="2xl"
							bg={useColorModeValue("rgba(14, 165, 233, 0.06)", "rgba(14, 165, 233, 0.08)")}
							border="1px solid"
							borderColor={useColorModeValue("blue.200", "rgba(14, 165, 233, 0.25)")}
							w="100%"
						>
							<Flex align="center" gap={4}>
								<Flex
									p={3}
									bg={useColorModeValue("#0ea5e9", "rgba(14, 165, 233, 0.2)")}
									color={useColorModeValue("white", "#38bdf8")}
									borderRadius="xl"
									align="center"
									justify="center"
									w="46px"
									h="46px"
									flexShrink={0}
								>
									<FontAwesomeIcon icon={faLock} size="lg" />
								</Flex>
								<Box flex={1}>
									<Heading size="xs" color={useColorModeValue("blue.800", "blue.300")} mb={1} fontWeight="bold">
										Visibility: Private (Restricted Access)
									</Heading>
									<Text fontSize="sm" color={useColorModeValue("blue.700", "blue.400")} mb={3}>
										This course is private. Students must meet the access criteria below to view or unlock it on the mobile app.
									</Text>
									
									<HStack spacing={4} flexWrap="wrap">
										{hasDomainRestr && (
											<Flex align="center" gap={2} fontSize="sm" fontWeight="semibold" color={useColorModeValue("blue.800", "blue.300")}>
												<FontAwesomeIcon icon={faShieldHalved} />
												<Text>Domain: <Code colorScheme="cyan">{editApp.orgId}</Code></Text>
											</Flex>
										)}
										{hasAccessCode && (
											<Flex align="center" gap={2} fontSize="sm" fontWeight="semibold" color={useColorModeValue("blue.800", "blue.300")}>
												<FontAwesomeIcon icon={faShieldHalved} />
												<Text>Access Code: <Code colorScheme="cyan">{editApp.hiddenCode}</Code></Text>
											</Flex>
										)}
										{!hasDomainRestr && !hasAccessCode && (
											<Text fontSize="xs" fontStyle="italic" color={useColorModeValue("blue.600", "blue.400")}>
												Default organization restricted access.
											</Text>
										)}
									</HStack>
								</Box>
							</Flex>
						</Box>
					)}

					<VStack spacing={4} align="stretch" pt={2}>
						<Text fontSize="sm" color={subtitleColor} textAlign="center">
							Ready to publish? Deploying syncs course metadata, structural changes, and files to the live production server.
						</Text>
						
						<Button
							size='lg'
							onClick={handleDeploymentClick}
							leftIcon={<FontAwesomeIcon icon={faRocket} />}
							bgGradient="linear(to-r, #0284c7, #0ea5e9, #10b981)"
							_hover={{
								bgGradient: "linear(to-r, #0369a1, #0284c7, #059669)",
								transform: "translateY(-2px)",
								boxShadow: "0 8px 24px rgba(14, 165, 233, 0.45)"
							}}
							_active={{
								transform: "translateY(0)"
							}}
							color="white"
							transition="all 0.2s"
							borderRadius="xl"
							py={7}
							fontSize="md"
							fontWeight="bold"
						> 
							Deploy Application 
						</Button>
					</VStack>

					<Divider my={4} borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")} />

					{/* Danger Zone */}
					<Box
						w="100%"
						p={6}
						borderRadius="2xl"
						border="1px solid"
						borderColor={useColorModeValue("red.200", "rgba(239, 68, 68, 0.25)")}
						bg={useColorModeValue("red.50/50", "rgba(239, 68, 68, 0.06)")}
					>
						<Flex justify="space-between" align="center" direction={{ base: "column", md: "row" }} gap={4}>
							<Box>
								<Heading size="xs" color={useColorModeValue("red.800", "red.300")} mb={1} display="flex" alignItems="center" gap={2} fontWeight="bold">
									<FontAwesomeIcon icon={faExclamationTriangle} /> Danger Zone: Delete Deployment
								</Heading>
								<Text fontSize="xs" color={useColorModeValue("gray.600", "gray.400")} maxW="550px">
									Removes the course from the live mobile app immediately. Students will lose access. Your local draft and assets inside the course editor are unaffected.
								</Text>
							</Box>
							
							<Button
								size="md"
								colorScheme="red"
								variant="outline"
								borderColor="rgba(239, 68, 68, 0.4)"
								color="red.400"
								onClick={() => setIsDeleteAlertOpen(true)}
								isLoading={isDeleting}
								leftIcon={<FontAwesomeIcon icon={faTrash} />}
								_hover={{
									bg: "rgba(239, 68, 68, 0.15)",
									color: "red.300",
									borderColor: "rgba(239, 68, 68, 0.6)",
									transform: "translateY(-1px)",
									boxShadow: "sm"
								}}
								_active={{
									transform: "translateY(0)"
								}}
								transition="all 0.2s"
								borderRadius="xl"
							> 
								Delete Deployment 
							</Button>
						</Flex>
					</Box>
				</CardBody>
			</Card>
		</Center>

		<AlertDialog
			isOpen={isDeleteAlertOpen}
			leastDestructiveRef={cancelRef}
			onClose={() => setIsDeleteAlertOpen(false)}
		>
			<AlertDialogOverlay backdropFilter="blur(5px)" bg="rgba(0, 0, 0, 0.65)">
				<AlertDialogContent borderRadius="2xl" mt="20vh" bg={useColorModeValue("white", "#131b26")} border="1px solid" borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")} color={useColorModeValue("gray.800", "white")}>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						Delete Deployment?
					</AlertDialogHeader>

					<AlertDialogBody color={useColorModeValue("gray.600", "gray.300")}>
						Are you sure you want to delete the deployment for <strong>{editApp.name}</strong>?<br/><br/>
						This will remove the course and all associated active flashcard quests from the live mobile app immediately. Students will lose access.
					</AlertDialogBody>

					<AlertDialogFooter>
						<Button ref={cancelRef} onClick={() => setIsDeleteAlertOpen(false)} borderRadius="xl" isDisabled={isDeleting} variant="ghost">
							Cancel
						</Button>
						<Button colorScheme="red" onClick={handleDeleteDeployment} ml={3} borderRadius="xl" isLoading={isDeleting} leftIcon={<FontAwesomeIcon icon={faTrash} />}>
							Delete
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialogOverlay>
		</AlertDialog>
		</>
	)
}
